/**
 * The dash page a click is on its way to, from the moment it is clicked.
 *
 * The rail used to stay on the old page until the new one had loaded: the
 * active item comes from RouterProvider's pathname, which only moves on
 * routeChangeComplete, and nothing on screen answered the click in between.
 * On a cold server that was seconds of a dead click (owner, 2026-10-26: "right
 * now it just stalls until everything loads. that sucks").
 *
 * Null when no navigation is under way. Shallow changes (a tab or filter
 * written into the query) and changes that stay on the same page are not
 * navigations here: the page is already showing and answers those itself.
 */
import { useEffect, useState } from 'react';
import Router from 'next/router';

/** The path of a URL Next hands its router events, without query or hash. */
export function pathOf(url: string): string {
  return url.split(/[?#]/)[0] || '/';
}

/** Whether a route change to `url` is a move to another dash page. */
export function isDashPageChange(url: string, fromPath: string, shallow: boolean): boolean {
  if (shallow) return false;
  const to = pathOf(url);
  if (to !== '/dash' && !to.startsWith('/dash/')) return false;
  return to !== pathOf(fromPath);
}

export function useDashPendingRoute(): string | null {
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    // The singleton's events exist before any router mounts (and in tests),
    // and are the same emitter the mounted router fires.
    const events = Router.events;
    if (!events) return undefined;
    const start = (url: string, { shallow }: { shallow: boolean }) => {
      // At routeChangeStart the address bar still shows the page being left.
      if (isDashPageChange(url, window.location.pathname, shallow)) setPending(pathOf(url));
    };
    const done = () => setPending(null);
    events.on('routeChangeStart', start);
    events.on('routeChangeComplete', done);
    events.on('routeChangeError', done);
    return () => {
      events.off('routeChangeStart', start);
      events.off('routeChangeComplete', done);
      events.off('routeChangeError', done);
    };
  }, []);

  return pending;
}
